import { HeartIcon, LockClosedIcon, ShieldCheckIcon } from "@heroicons/react/24/outline";
import { useSearchParams } from "react-router-dom";
import { DonationForm } from "../components/donation/DonationForm";
import { useDocumentMeta } from "../lib/meta";
import { PageIntro } from "../components/ui/PageIntro";
import { StatusBanner } from "../components/ui/StatusBanner";

const givingHighlights = [
  {
    title: "What giving supports",
    body: "Donations help sustain educational media, workshops, partnerships, community learning, and the nonprofit infrastructure behind mission-aligned project support.",
    Icon: HeartIcon,
  },
  {
    title: "Secure payment handling",
    body: "Card details never pass through this server. Stripe uses hosted checkout, and PayPal order creation and capture are finalized through verified server-side calls.",
    Icon: LockClosedIcon,
  },
  {
    title: "Recognition controls",
    body: "Public donor recognition is opt-in only. Names appear on the donor wall only for confirmed gifts and only when consent has been explicitly given.",
    Icon: ShieldCheckIcon,
  },
] as const;

export const DonatePage = () => {
  const [searchParams] = useSearchParams();
  const donationStatus = searchParams.get("status");

  useDocumentMeta({
    title: "Donate",
    description:
      "Support The Education Project's lifelong learning ecosystem through secure online giving with Stripe or PayPal.",
  });

  return (
    <div className="flex flex-col bg-white">
      {/* Full-bleed Photo Hero Banner */}
      <PageIntro
        eyebrow="Support Lifelong Learning"
        title="Invest in the people, programs, media, and partnerships that help learning keep going."
        summary="Giving to The Education Project helps strengthen a broader lifelong learning ecosystem. Donations can support educational initiatives, public-serving media, community engagement, and the nonprofit infrastructure needed to carry mission-aligned work forward."
      />

      {/* Section 1: Donation Form & Info - White Background */}
      <section className="bg-white py-16 md:py-24">
        <div className="mx-auto max-w-6xl px-6 lg:px-8">
          {donationStatus === "cancelled" ? (
            <div className="mb-8">
              <StatusBanner
                tone="neutral"
                title="Donation not completed"
                message="Your payment session was cancelled before confirmation. You can review the form and try again whenever you are ready."
              />
            </div>
          ) : null}

          <div className="grid gap-12 lg:grid-cols-[1fr_1.1fr] lg:items-start">
            <div className="divide-y divide-stone-200 border-t border-stone-200">
              {givingHighlights.map(({ title, body, Icon }) => (
                <article
                  key={title}
                  className="py-6 flex items-start gap-4"
                >
                  <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-[#0293cc]/10 text-[#0293cc]">
                    <Icon className="h-5 w-5" />
                  </div>
                  <div>
                    <h2 className="text-xl font-display font-bold text-[#0E1C2C]">{title}</h2>
                    <p className="mt-2 text-sm text-stone-600 leading-relaxed">{body}</p>
                  </div>
                </article>
              ))}
            </div>

            <div className="border border-stone-200 bg-[#F5F9FB] rounded-2xl p-6 md:p-8 shadow-sm">
              <DonationForm />
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};
